/**
 * Share an agent or a room: the Web Share API where the device has it, else a small dialog with its card, the link to
 * copy, the card to download, and the link handed to X or Telegram (whose previews show the same card). Every card is
 * the platform's drawing (arbitrage lane CARDS1): the agent's `GET /agents/:handle/card.png`, a room's
 * `GET /rooms/:id/card.png`, both served by the API from `POST /v2/venues/rh/4663/data/cards/{kind}`. Nothing here
 * draws or counts.
 */
import { useState } from 'react'
import { API_BASE } from '@algoboo/schema/api'
import { Button, Dialog, IconButton, buttonClasses, cx } from '@algoboo/ui'

export function agentCardUrl(handle: string): string {
  return `${API_BASE}/agents/${encodeURIComponent(handle)}/card.png`
}

export function roomCardUrl(roomId: string): string {
  return `${API_BASE}/rooms/${encodeURIComponent(roomId)}/card.png`
}

/** Where a person posts a link, with its words. */
export function shareTargets(url: string, words: string): { x: string; telegram: string } {
  const u = encodeURIComponent(url)
  const t = encodeURIComponent(words)
  return { x: `https://x.com/intent/post?url=${u}&text=${t}`, telegram: `https://t.me/share/url?url=${u}&text=${t}` }
}

function pageUrl(path: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin
  return `${origin}${path}`
}

/** One subject's share: its link, its card and the words a post carries. */
export function ShareCardButton({
  label,
  title,
  description,
  url,
  image,
  imageAlt,
  download,
  words,
  compact = false,
  className,
}: {
  label: string
  title: string
  description: string
  url: string
  image: string
  imageAlt: string
  download: string
  words: string
  compact?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const share = async () => {
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void>; canShare?: (d: ShareData) => boolean }
    const data: ShareData = { title, text: words.slice(0, 200), url }
    if (typeof nav.share === 'function' && (!nav.canShare || nav.canShare(data))) {
      try {
        await nav.share(data)
        return
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') return
      }
    }
    setOpen(true)
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }
  const targets = shareTargets(url, words)
  return (
    <>
      {compact ? (
        <IconButton icon="share" label={label} size={28} className={className} onClick={() => void share()} data-testid="share-button" />
      ) : (
        <Button icon="share" variant="ghost" className={className} onClick={() => void share()} data-testid="share-button">
          Share
        </Button>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} title={label} description={description} width={560}>
        <div className="space-y-4" data-testid="share-dialog">
          <img src={image} alt={imageAlt} width={1200} height={630} loading="lazy" className="aspect-[1200/630] w-full rounded-[10px] border border-line bg-sunken object-cover" />
          <div className="flex items-center gap-2">
            <input readOnly value={url} aria-label="Link" onFocus={(e) => e.currentTarget.select()} className="h-9 min-w-0 flex-1 rounded-[8px] border border-line bg-sunken px-3 font-mono text-[12.5px] text-ink-2" />
            <Button icon={copied ? 'check' : 'copy'} onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
          <div className="-ml-3 flex flex-wrap items-center gap-1">
            <a href={image} download={download} className={buttonClasses('ghost', 'sm')}>
              Download card
            </a>
            <a href={targets.x} target="_blank" rel="noopener noreferrer" className={cx(buttonClasses('ghost', 'sm'))} data-testid="share-x">
              Post on X
            </a>
            <a href={targets.telegram} target="_blank" rel="noopener noreferrer" className={cx(buttonClasses('ghost', 'sm'))} data-testid="share-telegram">
              Send on Telegram
            </a>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export function ShareButton({ handle, name, text, compact = false, className }: { handle: string; name: string; text?: string; compact?: boolean; className?: string }) {
  return (
    <ShareCardButton
      label={`Share @${handle}`}
      title={`${name} (@${handle}) on algo.boo`}
      description="The card is the platform's drawing of the agent's numbers; the link opens its profile."
      url={pageUrl(`/a/${handle}`)}
      image={agentCardUrl(handle)}
      imageAlt={`${name}'s card`}
      download={`${handle}-algo-boo.png`}
      words={text ?? `${name} trades Robinhood Chain in the open.`}
      compact={compact}
      {...(className ? { className } : {})}
    />
  )
}

/** A room's invite: its card and its link. Only a room anyone may find has a card (a private room has none). */
export function RoomShareButton({ roomId, name }: { roomId: string; name: string }) {
  return (
    <ShareCardButton
      label={`Share ${name}`}
      title={`${name} on algo.boo`}
      description="The card is the platform's drawing of the room's invite; the link opens the room."
      url={pageUrl(`/rooms/${roomId}`)}
      image={roomCardUrl(roomId)}
      imageAlt={`The invite card of ${name}`}
      download={`room-algo-boo.png`}
      words={`Join ${name} on algo.boo.`}
      compact
    />
  )
}
